import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy
} from '@angular/core';
import {CdkDrag, CdkDragDrop, CdkDropList, CdkDropListGroup} from "@angular/cdk/drag-drop";
import {Task, TaskControllerService} from "../../../api";
import {ToolbarService} from "../../util/design/toolbar/toolbar.service";
import {TaskDetailsService} from "../../../services/tasks/task-details.service";
import {StateConfigService} from "../../../services/state/state-config.service";
import {TaskStatePipe} from "../../../pipes/task-state.pipe";
import {TaskSseService} from "../../../services/tasks/task-sse.service";
import {PermissionService} from "../../../services/permission/permission.service";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {finalize} from "rxjs/operators";
import {
  faCircle,
  faCircleCheck,
  faCircleHalfStroke,
  faCircleInfo,
  faClipboardCheck,
  faMoonFull,
  faPlus,
  faX
} from "@fortawesome/pro-solid-svg-icons";
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from "@angular/forms";
import {initFlowbite} from "flowbite";
import {KeyValuePipe} from "@angular/common";
import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {TaskCardComponent} from "../task-card/task-card.component";
import {BicButtonComponent, TooltipDirective} from "@buildinginformationcloud/bic-ui-components";
import {EmptyStateComponent} from "../../util/design/empty-state/empty-state.component";
import {NotificationsService} from "../../../services/notifications/notifications.service";
import {sortTasks, TaskSortDirection, TaskSortField} from "../task-sort";
import StateEnum = Task.StateEnum;
import PriorityEnum = Task.PriorityEnum;

interface TasksState {
  tasks: Task[],
  state: StateEnum,
}

@Component({
  selector: 'app-task-board',
  templateUrl: './task-board.component.html',
  styleUrls: ['./task-board.component.scss'],

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    KeyValuePipe,
    TaskStatePipe,
    FaIconComponent,
    TaskCardComponent,
    BicButtonComponent,
    EmptyStateComponent,
    CdkDropList,
    CdkDropListGroup,
    CdkDrag,
    TooltipDirective
  ]
})
export class TaskBoardComponent implements OnInit, OnChanges {
  stateConfigService = inject(StateConfigService);
  formBuilder = inject(UntypedFormBuilder);
  taskControllerService = inject(TaskControllerService);
  toolbar = inject(ToolbarService);
  taskDetailService = inject(TaskDetailsService);
  protected permissionService = inject(PermissionService);
  private taskSseService = inject(TaskSseService);
  private notificationsService = inject(NotificationsService);
  private destroyRef = inject(DestroyRef);


  @Input() projectId: number | undefined;
  @Input() selectionMode: boolean = false;
  @Output() importBCFClicked: EventEmitter<any> = new EventEmitter<any>();
  @Output() exportBCFClicked: EventEmitter<Task> = new EventEmitter<Task>();
  @Output() exportBCFsClicked: EventEmitter<Task[]> = new EventEmitter<Task[]>();
  @Output() selectedTasksChanged: EventEmitter<Task[]> = new EventEmitter<Task[]>();
  @Output() assignedUsersChanged: EventEmitter<any[]> = new EventEmitter<any[]>();
  @Output() resetSearchClicked: EventEmitter<void> = new EventEmitter<void>();
  @Input() searchAndFilter: {
    "search": string,
    "state": StateEnum[] | undefined,
    "priority": PriorityEnum[] | undefined,
    "dueDate": any[] | undefined,
    "assignedTo": any[]
  } = {search: "", state: undefined, priority: undefined, dueDate: undefined, assignedTo: []};

  /**
   * Sortierung aus der Kopfzeile. Sie ist der Grund, dass eine Aufgabe nach dem Zuweisen einer
   * Person an ihrem Platz bleibt: taskChanged() haengt den geaenderten Task hinten an, erst die
   * Sortierung stellt die Reihenfolge wieder her.
   */
  @Input() sortField: TaskSortField = 'name';
  @Input() sortDirection: TaskSortDirection = 'asc';

  tasks: Map<string, Task[]> = new Map<Task.StateEnum, Task[]>()
  filteredTasks: Map<string, Task[]> = new Map<Task.StateEnum, Task[]>()

  loading = true;

  newTaskForm!: UntypedFormGroup

  stateEnum: typeof StateEnum = StateEnum;

  selectedTasks: Task[] = []

  constructor() {
    const toolbar = this.toolbar;

    toolbar.config = {
      add: {
        disabled: () => !this.permissionService.hasProjectPrivilege('PROJECT_TASK_EDIT'),
        routerLink: ['/tasks/create']
      }
    }
  }

  ngOnInit(): void {
    initFlowbite()

    this.newTaskForm = this.formBuilder.group({
      id: new UntypedFormControl(),
      informedUsers: new UntypedFormControl([]),
      project: new UntypedFormControl(),
      projectID: new UntypedFormControl(),
      state: new UntypedFormControl(StateEnum.OPEN, Validators.required),
      lph: new UntypedFormControl(),
      priority: new UntypedFormControl(PriorityEnum.NORMAL), // New property
      ifcName: new UntypedFormControl(),
      ifcLink: new UntypedFormControl(),
      markup: this.formBuilder.group({
        id: new UntypedFormControl(),
        comment: this.formBuilder.array([]),
        topic: this.formBuilder.group({
          atGuid: new UntypedFormControl(""),
          atTopicType: new UntypedFormControl(),
          atTopicStatus: new UntypedFormControl(),
          referenceLink: new UntypedFormControl(),
          title: new UntypedFormControl(''),
          priority: new UntypedFormControl(),
          index: new UntypedFormControl(),
          labels: new UntypedFormControl(),
          creationDate: new UntypedFormControl(),
          creationAuthor: new UntypedFormControl(),
          modifiedDate: new UntypedFormControl(),
          modifiedAuthor: new UntypedFormControl(),
          dueDate: new UntypedFormControl(),
          startDate: new UntypedFormControl(),
          assignedTo: new UntypedFormControl(),
          description: new UntypedFormControl(),
          creationUser: new UntypedFormControl(),
          modifiedUser: new UntypedFormControl(),
          assignedToUser: new UntypedFormControl(),
        }),
      }),
    });
    this.refreshList();

    const projectId = this.stateConfigService.getProjectIdOrUndefined();
    if (projectId) {
      this.taskSseService.subscribeToProject(projectId);
      this.taskSseService.taskEvents$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
        if (event.type === 'TASK_CREATED') {
          const state = event.data.state ?? StateEnum.OPEN;
          const list = this.tasks.get(state) ?? [];
          this.tasks.set(state, [...list, event.data]);
        } else if (event.type === 'TASK_UPDATED') {
          this.taskChanged(event.data);
        } else if (event.type === 'TASK_DELETED') {
          const deletedId = event.data as unknown as string;
          for (const state of this.tasks.keys()) {
            this.tasks.set(state, (this.tasks.get(state) ?? []).filter(t => t.id !== deletedId));
          }
        }
        this.filteredTasks = new Map(this.tasks);
        this.filterTasksBySearchAndFilter();
        this.emitAssignedUsers();
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const sortChanged = (changes['sortField'] && !changes['sortField'].firstChange)
      || (changes['sortDirection'] && !changes['sortDirection'].firstChange);

    if ((changes['searchAndFilter'] && !changes['searchAndFilter'].firstChange) || sortChanged) {
      this.filterTasksBySearchAndFilter();
    }
  }

  /**
   * Die Daten je Spalte, zwischen zwei Change-Detection-Durchlaeufen stabil.
   *
   * Hintergrund: `taskStateFrom` haengt im Template an `[cdkDropListData]`.
   * Vorher lieferte es bei jedem Aufruf ein neues Objektliteral - Angular
   * vergleicht Inputs ueber die Referenz, die Drop-Liste galt damit in *jedem*
   * Durchlauf als geaendert und synchronisierte ihre Drag-Elemente neu. Im
   * Development-Modus fand `checkNoChanges` danach wieder ein anderes Objekt.
   * Mit null Tasks blieb das folgenlos (nichts zu synchronisieren), ab einem
   * Task fror die Seite ein - genau das Muster, das auf dem Dashboard schon die
   * Task-Kachel lahmgelegt hatte.
   */
  private readonly dropListData = new Map<StateEnum, TasksState>();

  taskStateFrom(tasks: Task[], state: StateEnum): TasksState {
    // Neues Objekt nur, wenn sich das Array wirklich geaendert hat. Die
    // Drop-Logik ersetzt die Arrays in `this.tasks` bei jeder Aenderung durch
    // neue (siehe drop()), dadurch wird der Cache genau dann ungueltig, wenn
    // es noetig ist.
    const cached = this.dropListData.get(state);
    if (cached && cached.tasks === tasks) {
      return cached;
    }

    const fresh: TasksState = {tasks, state};
    this.dropListData.set(state, fresh);
    return fresh;
  }

  refreshList() {
    this.loading = true;
    this.taskDetailService.clearOpenedTask();
    this.taskControllerService.getTasks(this.stateConfigService.getProjectIdOrUndefined(), this.stateConfigService.getOrganisationIdOrUndefined(), this.stateConfigService.isPersonal).pipe(
      finalize(() => this.loading = false)
    ).subscribe(tasks => {
      // tasks.forEach(t=>{t.state = this.stateEnum.OPEN})
      for (let stateEnumKey in this.stateEnum) {
        this.tasks.set(stateEnumKey, tasks.filter(t => t.state == stateEnumKey))
      }
      this.filterTasksBySearchAndFilter();
      this.emitAssignedUsers();

      this.taskDetailService.currentTaskChanged.subscribe(task => {
        if (task) {
          let listForNewTask = this.tasks.get(task.state!);
          listForNewTask?.push(task);
          this.tasks.set(task.state!, listForNewTask!);
          this.taskChanged(task)
          this.filteredTasks = this.tasks;
          this.filterTasksBySearchAndFilter();
          this.emitAssignedUsers();
        }
      });
      this.taskDetailService.currentTaskDeleted.subscribe(task => {
        if (task) {
          let listForNewTask = this.tasks.get(task.state!);
          listForNewTask = listForNewTask?.filter(t => t.id !== task.id);
          this.tasks.set(task.state!, listForNewTask!);
          this.filteredTasks = this.tasks;
          this.filterTasksBySearchAndFilter();
          this.emitAssignedUsers();
        }
      });
    });
  }

  taskChanged(task: Task) {
    for (const state of this.tasks.keys()) {
      let taskValues = this.tasks.get(state);
      if (taskValues?.find(t => t.id === task.id)) {
        taskValues = taskValues!.filter(t => t.id !== task.id);
        this.tasks.set(state, taskValues!)
      }
    }

    // Den Task zur Liste mit dem neuen Status hinzufügen
    const newState = task.state ? task.state : StateEnum.OPEN;
    const newTaskValues = this.tasks.get(newState) || [];
    newTaskValues.push(task);
    this.tasks.set(newState, newTaskValues);
    this.filteredTasks = this.tasks;
    this.filterTasksBySearchAndFilter()
  }

  createNewTask(state: StateEnum = StateEnum.OPEN) {
    let task: Task = {state: state}
    this.taskDetailService.setOpenedTask(task);
  }


  drop(event: CdkDragDrop<TasksState, TasksState, Task>) {
    const previousTasks = this.tasks.get(event.previousContainer.data.state) ?? [];
    const containerTasks = this.tasks.get(event.container.data.state) ?? [];
    const movedTask = event.item.data ?? event.previousContainer.data?.tasks?.[event.previousIndex];
    const previousState = movedTask?.state;

    if (!movedTask) {
      this.notificationsService.showErrorMessage('Die Aufgabe konnte nicht gespeichert werden.');
      return;
    }

    const previousTaskIndex = previousTasks.findIndex(task => task.id === movedTask.id);
    if (previousTaskIndex < 0) {
      this.notificationsService.showErrorMessage('Die Aufgabe konnte nicht gespeichert werden.');
      return;
    }

    previousTasks.splice(previousTaskIndex, 1);
    containerTasks.splice(Math.min(event.currentIndex, containerTasks.length), 0, movedTask);
    this.tasks.set(event.previousContainer.data.state, previousTasks);
    this.tasks.set(event.container.data.state, containerTasks);

    movedTask.state = event.container.data.state;
    // Gerendert wird filteredTasks, verschoben wurde in tasks: die sortierte Ansicht muss neu
    // aufgebaut werden, sonst springt die Karte erst mit dem SSE-Update an ihren neuen Platz.
    this.filterTasksBySearchAndFilter();

    const rollback = () => {
      const movedTaskIndex = containerTasks.findIndex(task => task.id === movedTask.id);
      if (movedTaskIndex >= 0) {
        containerTasks.splice(movedTaskIndex, 1);
      }
      previousTasks.splice(Math.min(event.previousIndex, previousTasks.length), 0, movedTask);
      this.tasks.set(event.previousContainer.data.state, previousTasks);
      this.tasks.set(event.container.data.state, containerTasks);
      movedTask.state = previousState;
      this.filterTasksBySearchAndFilter();
    };

    if (!movedTask.id || movedTask.id === '0') {
      rollback();
      this.notificationsService.showErrorMessage('Die Aufgabe konnte nicht gespeichert werden.');
      return;
    }

    this.taskControllerService.updateTask(
      movedTask,
      "",
      this.stateConfigService.getProjectIdOrUndefined() ?? (movedTask.projectId ?? -1)
    ).subscribe({
      error: () => {
        rollback();
        this.notificationsService.showErrorMessage('Der Status der Aufgabe konnte nicht gespeichert werden.');
      }
    });
  }

  tasksArray(): Task[] {
    let tasks: Task[] = [];
    for (let stateEnumKey in this.stateEnum) {
      if (this.tasks.has(stateEnumKey)) {
        tasks.push(...this.tasks.get(stateEnumKey)!)
      }
    }

    return tasks;
  }

  hasAnyTasks(): boolean {
    for (let stateEnumKey in this.stateEnum) {
      if (this.tasks.has(stateEnumKey) && this.tasks.get(stateEnumKey)!.length > 0) {
        return true;
      }
    }
    return false;
  }

  hasAnyFilteredTasks(): boolean {
    for (let stateEnumKey in this.stateEnum) {
      if (this.filteredTasks.has(stateEnumKey) && this.filteredTasks.get(stateEnumKey)!.length > 0) {
        return true;
      }
    }
    return false;
  }

  hasActiveSearchOrFilter(): boolean {
    const {search, state, priority, dueDate, assignedTo} = this.searchAndFilter;
    return (
      !!search ||
      (state && state.length > 0) ||
      (priority && priority.length > 0) ||
      (dueDate && dueDate.length > 0) ||
      (assignedTo && assignedTo.length > 0)
    );
  }

  showResetSearchButton(): boolean {
    return this.hasActiveSearchOrFilter() && !this.hasAnyFilteredTasks();
  }

  taskCount(state: StateEnum): number {
    return this.filteredTasks.get(state)?.length ?? 0;
  }

  keepOrder = (a: any, _: any) => {
    return a;
  }

  selectTask(task: Task){
    const index: number = this.selectedTasks.indexOf(task);
    if (index !== -1) {
      this.selectedTasks.splice(index, 1);
    } else {
      this.selectedTasks.push(task)
    }
  }

  selectColumnTasks(state: unknown){
    // @ts-ignore
    let columnTasks = this.tasks.get(state.value);
    if(columnTasks){
      for (let task of columnTasks){
        if(!this.selectedTasks.includes(task)){
          this.selectedTasks.push(task)
        }
      }
    }
  }

  deselectColumnTasks(state: unknown){
    // @ts-ignore
    let columnTasks = this.tasks.get(state.value);
    if(columnTasks){
      for (let task of columnTasks){
        if(this.selectedTasks.includes(task)){
          this.selectedTasks = this.selectedTasks.filter(t => t !== task)
        }
      }
    }
  }

  areAllColumnTasksSelected(state: unknown){
    // @ts-ignore
    let selected = this.selectedTasks.filter(task => task.state === state.key)
    // @ts-ignore
    let columnTasks = this.tasks.get(state.value);
    return selected.length === columnTasks?.length
  }



  emitSelectedTasks() {
    this.selectedTasksChanged.emit(this.selectedTasks);
  }

  isTaskSelected(task:Task){
    return this.selectedTasks.includes(task)
  }

  emitAssignedUsers() {
    const usersSet = new Set<any>();
    for (let tasks of this.tasks.values()) {
      for (let task of tasks) {
        const assignedUser = task.assignedToUser;
        if (assignedUser) {
          let assigned = assignedUser.firstName + " " + assignedUser.lastName;
          usersSet.add(assigned)
        }
      }
    }
    this.assignedUsersChanged.emit(Array.from(usersSet));
  }

  filterTasksBySearchAndFilter(): void {
    const {search, state, priority, dueDate, assignedTo} = this.searchAndFilter;

    if (!this.hasActiveSearchOrFilter()) {
      this.filteredTasks = this.sortColumns(this.tasks);
      return;
    }

    const lowerCaseSearch = search ? search.toLowerCase() : '';
    const newFiltered = new Map<string, Task[]>();

    this.tasks.forEach((tasks, stateKey) => {
      let filteredTasksForState = tasks;

      if (search) {
        filteredTasksForState = filteredTasksForState.filter(task =>
          task.title &&
          task.title.toLowerCase().includes(lowerCaseSearch)
        );
      }

      if (state && state.length > 0) {
        filteredTasksForState = filteredTasksForState.filter(task =>
          state.includes(task.state as StateEnum)
        );
      }

      if (priority && priority.length > 0) {
        filteredTasksForState = filteredTasksForState.filter(task =>
          priority.includes(task.priority as PriorityEnum)
        );
      }

      if (dueDate && dueDate.length > 0) {
        filteredTasksForState = filteredTasksForState.filter(task => {
          const taskDueDate = task.dueDate;
          if (!taskDueDate) return false;
          if (Array.isArray(dueDate)) {
            // Match if any selected case applies
            return dueDate.some((range: string) => this.isDateInRange(taskDueDate, range));
          } else {
            return this.isDateInRange(taskDueDate, dueDate);
          }
        });
      }

      if (assignedTo && assignedTo.length > 0) {
        filteredTasksForState = filteredTasksForState.filter(task => {
          const assignedUser = task.assignedToUser;
          if (!assignedUser) return false;
          let assigned = assignedUser.firstName + ' ' + assignedUser.lastName;
          return assignedTo.includes(assigned);
        });
      }

      newFiltered.set(stateKey, filteredTasksForState);
    });

    this.filteredTasks = this.sortColumns(newFiltered);
  }

  /** Sortiert jede Board-Spalte einzeln nach dem aktuell gewaehlten Kriterium. */
  private sortColumns(tasksByState: Map<string, Task[]>): Map<string, Task[]> {
    const sorted = new Map<string, Task[]>();
    tasksByState.forEach((tasks, stateKey) => {
      sorted.set(stateKey, sortTasks(tasks, this.sortField, this.sortDirection));
    });
    return sorted;
  }

  onResetSearch(): void {
    this.searchAndFilter = {
      search: '',
      state: undefined,
      priority: undefined,
      dueDate: undefined,
      assignedTo: []
    };
    this.filterTasksBySearchAndFilter();
    this.resetSearchClicked.emit();
  }

  private isDateInRange(value: any, range: string): boolean {
    if (!value) return false;

    const date = new Date(value);
    if (isNaN(date.getTime())) return false;

    const d = new Date(date);
    d.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);

    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    const threeSixtyFiveDaysAgo = new Date(today);
    threeSixtyFiveDaysAgo.setDate(today.getDate() - 365);

    switch (range) {
      case 'Heute':
        return d.getTime() === today.getTime();
      case 'Gestern':
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        return d.getTime() === yesterday.getTime();
      case 'Letzte 7 Tage':
        return d >= sevenDaysAgo && d <= today;
      case 'Letzte 30 Tage':
        return d >= thirtyDaysAgo && d <= today;
      case 'Dieses Jahr':
        return d >= threeSixtyFiveDaysAgo && d <= today;
      case 'Letztes Jahr':
        const startOfThisYear = new Date(today.getFullYear(), 0, 1);
        const endOfLastYear = new Date(today.getFullYear() - 1, 11, 31);
        return d >= new Date(startOfThisYear.getFullYear() - 1, 0, 1) && d <= endOfLastYear;
      case 'Vor diesem Jahr':
        return d < threeSixtyFiveDaysAgo;
      default:
        if (range.length >= 10) {
          const filterDate = range.length > 10 ? range.substring(0, 10) : range;
          const taskDate = value.length > 10 ? value.substring(0, 10) : value;
          return taskDate === filterDate;
        }
        return false;
    }
  }

  onTaskDuplicated(task: Task) {
    this.taskControllerService.updateTask(task, "", task.projectId ? task.projectId : -1).subscribe(next => {
      let listForNewTask = this.tasks.get(next.state!);
      if (listForNewTask) {
        listForNewTask.push(next);
        this.tasks.set(next.state!, listForNewTask!);
        this.filteredTasks = this.tasks;
        this.filterTasksBySearchAndFilter();
        this.emitAssignedUsers();
      }
    });
  }

  protected readonly TaskStatePipe = TaskStatePipe;
  protected readonly faPlus = faPlus;
  protected readonly faX = faX;
  protected readonly faCircleHalfStroke = faCircleHalfStroke;
  protected readonly faCircleCheck = faCircleCheck;
  protected readonly faCircle = faCircle;
  protected readonly faMoonFull = faMoonFull;
  protected readonly faCircleInfo = faCircleInfo;
  protected readonly faClipboardCheck = faClipboardCheck;
}
